import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import {
  OpoForm,
  DEFAULT_OPO_VALUES,
  NewOpoForm,
  useOpoFormEditSchema,
  getOpoOrganizationBody,
} from '@lp/features/admin/forms/OPOForm';
import { PrimaryContactForm } from '@lp/features/admin/forms/PrimaryContactForm';
import { AuthContext } from '@lp/core/auth';
import { OpoOrganization, PatchOpoOrganizationRequestBody } from '@/api/apis/opo';
import { Contact } from '@/api/apis/facilities';
import { useUpdateOpoMutation } from '@/queries/opo';
import { useWrappedRemoveOpoMutation } from '@/queries/orchestrator';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { getChangedValues, getShortTzByIanaName } from '@/utils/helpers';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';

export const OpoEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<OpoOrganization>();
  const { mutateAsync } = useUpdateOpoMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasOpoCreatePermissions = checkPermission(AdminPermissions.OpoCreate);
  const { schema, isLoading: isGeoDataLoading } = useOpoFormEditSchema();

  const defaultValues = useMemo<NewOpoForm>(() => {
    const contactList = data.contacts || [];
    const mainContact = contactList.find((c) => c.isPrimary);

    return {
      ...DEFAULT_OPO_VALUES,
      name: data.name,
      code: data.code || '',
      timezone: getShortTzByIanaName(data.location.timezone) || '',
      timezoneIANA: data.location.timezone,
      addressLine: data.location.addressLine,
      country: data.location.country,
      city: data.location.city,
      state: data.location.state,
      latitude: data.location.latitude || null,
      longitude: data.location.longitude || null,
      postalCode: data.location.postalCode,
      primaryContactId: mainContact?.id,
      firstName: mainContact?.firstName || 'Unnamed',
      lastName: mainContact?.lastName || 'Contact',
      email: mainContact?.email,
      photoPath: mainContact?.photoPath || '',
      primaryPhone: mainContact?.primaryPhone || '',
      secondaryPhone: mainContact?.secondaryPhone || '',
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/opos/locations');
  };

  const handleSubmit = async (values: NewOpoForm) => {
    try {
      const defaultBody = getOpoOrganizationBody(defaultValues);
      const updateBody = getOpoOrganizationBody(values);

      const changedValues = getChangedValues<PatchOpoOrganizationRequestBody>(updateBody, defaultBody);

      await mutateAsync({
        id: data.id,
        ...changedValues,
      });

      toast.success(`OPO updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    successMessage: 'Opo deleted successfully.',
    query: useWrappedRemoveOpoMutation,
    onDeleteCb: handleBackToList,
  });

  const handleOpoDelete = () => {
    onModalOpen(data.id);
  };

  if (isGeoDataLoading) return null;

  return (
    <>
      <Form<NewOpoForm> defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit OPO" backToListLink="/admin/opos/locations">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleOpoDelete}
              hasEditPermissions={hasOpoCreatePermissions}
              hasDeletePermissions={hasOpoCreatePermissions}
            />
          </AdminPageHeader>
          <OpoForm isReadOnly={!hasOpoCreatePermissions} />
          <PrimaryContactForm
            list={data.contacts as unknown as Contact[]}
            isReadOnly
            isDisabled={!hasOpoCreatePermissions}
          />
        </div>
      </Form>
      <DeleteModal
        title="Delete OPO?"
        description="Are you sure you want to delete the OPO?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
